import React, { useEffect, useMemo, useRef, useState } from "react";
import * as R from "ramda";
import { Content } from "@worcket/common/components/layout";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon, notification } from "antd";

import * as gtag from "../../../../lib/gtag";
import Page from "../../../../components/display/Page";
import UniversityList from "../../../../components/List/UniversityList";
import JobEmailForm from "../../NewOpportunity/JobEmailForm";
import AutoInviteExternalCandidates from "../../NewOpportunity/AutoInviteExternalCandidates";
import Button from "../../../../components/Button";
import {
  PUBLISH_OPPORTUNITY,
  saveOpportunityDetails,
  SAVE_OPPORTUNITY_DETAILS,
  publishOpportunity
} from "../../../../store/actions/opportunity";
import { isLoading } from "../../../../utils/actions";
import StatusIcon from "../../../../components/StatusIcon";

import MediaForm from "./MediaForm";
import DetailsForm from "./DetailsForm";
import CandidateForm from "./CandidateForm";
import ScreeningForm from "./ScreeningForm";
import PublishingForm from "./PublishingForm";
import MultipostingForm from "./MultipostingForm/MultipostingForm";

import styled from "styled-components/macro";

const Footer = styled.div`
  position: sticky;
  bottom: 0px;
  padding: 10px;
  height: 64px;
  display: flex;
  background: white;
  box-shadow: 0px 3px 12px #00000029;
  z-index: 10;
`;

const Container = styled.div`
  max-width: 1150px;
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  margin: auto;
`;

const StatusText = styled.span`
  color: ${({ theme }) => theme["@gray-60"]};
  margin-left: 10px;
  font-size: 12px;
`;

const ScreeningAlert = styled.p`
  color: ${({ theme }) => theme["@gray-60"]};
  margin: 0px;
  margin-right: 10px;
  width: 110px;
  font-size: 11px;
  text-align: right;

  &:hover {
    cursor: pointer;
    font-weight: bold;
  }
`;

const WrapperAlert = styled.div`
  display: flex;
  align-items: center;
`;

const IconAlert = styled(Icon)`
  font-size: 20px;
  color: ${({ theme }) => theme["@gray-50"]};
  margin-right: 20px;
`;

const OpportunityDetails = ({ opportunity, permissions, isAdmin, isOwner }) => {
  const company = useSelector(state => R.propOr({}, "company", state));
  const [isEditingScreening, setIsEditingScreeening] = useState(false);
  const isDisabled = !isAdmin && !isOwner && !R.includes("write", permissions);
  const ref = useRef(null);

  const { section } = useParams();
  let form = {};
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loading = useSelector(state =>
    isLoading(SAVE_OPPORTUNITY_DETAILS, state)
  );
  const loadingPublish = useSelector(state =>
    isLoading(PUBLISH_OPPORTUNITY, state)
  );

  const statusText = useMemo(
    () => ({
      open: t("opportunity:statuses.open"),
      published: t("opportunity:statuses.published"),
      finished: t("opportunity:statuses.finished")
    }),
    [t]
  );

  const isUnpublished = useMemo(
    () => opportunity.status === "open" || !opportunity.status,
    [opportunity.status]
  );

  useEffect(() => {
    gtag.event({
      category: "VacancyDetail",
      action: "VacancyDetail",
      label: opportunity.id
    });
  }, [opportunity.id]);

  return (
    <div style={{ position: "relative" }}>
      <Page>
        <DetailsForm
          opportunity={opportunity}
          isDisabled={isDisabled}
          setForm={data => {
            form = { ...form, ...data };
          }}
        />
        <MediaForm
          section={section}
          opportunity={opportunity}
          isDisabled={isDisabled}
          setForm={data => {
            form = { ...form, ...data };
          }}
        />
        <CandidateForm
          section={section}
          opportunity={opportunity}
          isDisabled={isDisabled}
          setForm={data => {
            form = { ...form, ...data };
          }}
        />
        <div ref={ref}>
          <ScreeningForm
            section={section}
            opportunity={opportunity}
            isDisabled={isDisabled}
            setIsEditingScreeening={setIsEditingScreeening}
            isEditingScreening={isEditingScreening}
          />
        </div>
        <Content>
          <MultipostingForm
            section={section}
            opportunity={opportunity}
            isDisabled={isDisabled}
            setForm={data => {
              form = { ...form, ...data };
            }}
          />
        </Content>
        {company.type !== "university" && (
          <Content>
            <UniversityList
              section={section}
              type="details"
              isDisabled={isDisabled}
              opportunity={opportunity}
              initialSelected={opportunity.publishedOnCompanies}
              setForm={data => {
                form = { ...form, ...data };
              }}
            />
          </Content>
        )}
        <Content>
          <JobEmailForm
            section={section}
            opportunity={opportunity}
            isDisabled={isDisabled}
          />
        </Content>
        <Content>
          <AutoInviteExternalCandidates
            opportunity={opportunity}
            isDisabled={isDisabled}
            setForm={data => {
              form = { ...form, ...data };
            }}
          />
        </Content>
        <PublishingForm
          section={section}
          opportunity={opportunity}
          isDisabled={isDisabled}
        />
      </Page>
      <Footer>
        <Container>
          <div>
            <StatusIcon status={opportunity.status} />
            <StatusText>{`${t("global:opportunity")} ${
              statusText[opportunity.status]
            }`}</StatusText>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            {!isEditingScreening && (
              <WrapperAlert>
                <ScreeningAlert
                  onClick={() =>
                    ref.current.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  {t("global:unsavedScreening")}
                </ScreeningAlert>
                <IconAlert type="info-circle" />
              </WrapperAlert>
            )}
            <Button
              size="lg"
              data-test="save-general-btn"
              type={isUnpublished ? "secondary" : "primary"}
              onClick={() => {
                if (
                  !form.hasSalaryRange ||
                  !form.salaryRange ||
                  R.equals(form.salaryRange, [undefined, undefined])
                ) {
                  form.salaryRange = [];
                }

                if (
                  form.description.trim() === "" ||
                  form.title.trim() === "" ||
                  form.title.trim().length > 75 ||
                  (form.hasSalaryRange &&
                    (form.salaryRange.length !== 2 ||
                      form.salaryRange[0] > form.salaryRange[1]))
                ) {
                  notification.error({
                    message: t("global:error"),
                    description: t(
                      "toasts:opportunity.saveOpportunityDetailsRejected"
                    )
                  });
                } else {
                  gtag.event({
                    category: "VacancyDetail",
                    action: "VacancyDetail.Detail-Save",
                    label: opportunity.id
                  });
                  delete form.hasSalaryRange;
                  if (!form.team) form.team = null;
                  dispatch(
                    saveOpportunityDetails({
                      id: opportunity.id,
                      formData: form
                    })
                  );
                }
              }}
              loading={loading}
              disabled={isDisabled || loadingPublish || !isEditingScreening}
            >
              {t("global:saveChanges")}
            </Button>
            {isUnpublished && (
              <Button
                data-test="publish-btn"
                type="primary"
                size="lg"
                style={{ marginLeft: 10 }}
                onClick={() => dispatch(publishOpportunity(opportunity.id))}
                disabled={isDisabled || loading || !isEditingScreening}
                loading={loadingPublish}
              >
                {t("opportunity:publishing.publish")}
              </Button>
            )}
          </div>
        </Container>
      </Footer>
    </div>
  );
};

export default OpportunityDetails;
